import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Switch,
  FormControlLabel,
  Autocomplete,
  Checkbox,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { usersService, municipiosService, perfisService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface User {
  id: number;
  nome: string;
  email: string;
  cpf?: string;
  ativo: boolean;
  is_admin?: boolean;
}

const UsersPage: React.FC = () => {
  const { municipioAtivo, user, hasPermission } = useAuth();
  const isSysAdmin = user?.is_admin === true || hasPermission('SYSTEM_ADMIN');

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Municípios disponíveis para o usuário logado
  const [availableMunicipios, setAvailableMunicipios] = useState<any[]>([]);
  const [selectedMunicipios, setSelectedMunicipios] = useState<number[]>([]);
  const [perfis, setPerfis] = useState<any[]>([]);
  const [perfisPorMunicipio, setPerfisPorMunicipio] = useState<{ [municipioId: number]: { [perfilId: number]: boolean } }>({});

  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    cpf: '',
    senha: '',
    ativo: true,
    is_admin: false,
  });

  const municipioAtivoId = municipioAtivo?.id;

  useEffect(() => {
    loadUsers();
    loadMunicipios();
    loadPerfis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [municipioAtivoId, isSysAdmin]);

  useEffect(() => {
    setPerfisPorMunicipio((prev) => {
      const updated = { ...prev };
      selectedMunicipios.forEach((municipioId) => {
        if (!updated[municipioId]) updated[municipioId] = {};
      });
      Object.keys(updated).forEach((key) => {
        if (!selectedMunicipios.includes(Number(key))) delete updated[Number(key)];
      });
      return updated;
    });
  }, [selectedMunicipios]);

  const loadMunicipios = async () => {
    try {
      if (isSysAdmin) {
        // Admin vê todos os municípios
        const data = await municipiosService.getMunicipios();
        setAvailableMunicipios(data);
      } else {
        // Coordenador vê apenas seus municípios
        const data = await municipiosService.getMunicipiosByUser();
        setAvailableMunicipios(data);
      }
    } catch (err) {
      console.error('Erro ao carregar municipios:', err);
    }
  };

  const loadPerfis = async () => {
    try {
      // Carregar perfis filtrados por município ativo
      const data = await perfisService.getPerfis(municipioAtivo?.id);
      setPerfis(data);
    } catch (err) {
      console.error('Erro ao carregar perfis:', err);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      // Admin vê todos os usuários; coordenador é filtrado pelo backend no seu escopo
      const municipioId = isSysAdmin ? undefined : municipioAtivo?.id;
      const data = await usersService.getUsers(municipioId);
      setUsers(data);
    } catch (error) {
      console.error('Erro ao carregar usuarios:', error);
      setError('Erro ao carregar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = async (userToEdit?: User) => {
    if (userToEdit) {
      setEditingUser(userToEdit);
      try {
        const userData = await usersService.getUser(userToEdit.id);
        setFormData({
          nome: userData.nome || '',
          email: userData.email || '',
          cpf: userData.cpf || '',
          senha: '',
          ativo: userData.ativo !== false,
          is_admin: userData.is_admin || false,
        });
        if (userData.municipios) {
          setSelectedMunicipios(userData.municipios.map((m: any) => m.id));
        } else {
          setSelectedMunicipios([]);
        }
        if (userData.perfis && userData.perfis.length > 0) {
          const perfisMap: { [municipioId: number]: { [perfilId: number]: boolean } } = {};
          userData.perfis.forEach((perfil: any) => {
            const municipioId = perfil.municipio_id;
            if (municipioId) {
              if (!perfisMap[municipioId]) perfisMap[municipioId] = {};
              perfisMap[municipioId][perfil.id] = true;
            }
          });
          setPerfisPorMunicipio(perfisMap);
        } else {
          setPerfisPorMunicipio({});
        }
      } catch (err) {
        setError('Erro ao carregar dados do usuario');
        return;
      }
    } else {
      setEditingUser(null);
      setFormData({ nome: '', email: '', cpf: '', senha: '', ativo: true, is_admin: false });
      // Pre-selecionar município ativo para coordenadores
      if (!isSysAdmin && municipioAtivo?.id) {
        setSelectedMunicipios([municipioAtivo.id]);
      } else {
        setSelectedMunicipios([]);
      }
      setPerfisPorMunicipio({});
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
    setFormData({ nome: '', email: '', cpf: '', senha: '', ativo: true, is_admin: false });
    setSelectedMunicipios([]);
    setPerfisPorMunicipio({});
  };

  const handleSave = async () => {
    try {
      const dataToSend: any = {
        nome: formData.nome,
        email: formData.email,
        ativo: formData.ativo,
      };

      if (formData.cpf) dataToSend.cpf = formData.cpf.replace(/\D/g, '');
      if (formData.senha) dataToSend.senha = formData.senha;
      if (selectedMunicipios.length > 0) dataToSend.municipio_ids = selectedMunicipios;

      // is_admin apenas se for SYSTEM_ADMIN
      if (isSysAdmin && formData.is_admin !== undefined) {
        dataToSend.is_admin = formData.is_admin;
      }

      // Perfis por município
      const perfilIds: Array<{ perfil_id: number; municipio_id: number }> = [];
      Object.keys(perfisPorMunicipio).forEach((municipioIdStr) => {
        const municipioId = Number(municipioIdStr);
        Object.keys(perfisPorMunicipio[municipioId]).forEach((perfilIdStr) => {
          if (perfisPorMunicipio[municipioId][Number(perfilIdStr)]) {
            perfilIds.push({ perfil_id: Number(perfilIdStr), municipio_id: municipioId });
          }
        });
      });
      if (perfilIds.length > 0) dataToSend.perfil_ids = perfilIds;

      if (editingUser) {
        await usersService.updateUser(editingUser.id, dataToSend);
        setSuccess('Usuario atualizado com sucesso!');
      } else {
        await usersService.createUser(dataToSend);
        setSuccess('Usuario criado com sucesso!');
      }

      handleCloseDialog();
      loadUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao salvar usuario');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este usuario?')) return;
    try {
      await usersService.deleteUser(id);
      setSuccess('Usuario excluido com sucesso!');
      loadUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao excluir usuario');
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4">Gestao de Usuarios</Typography>
          {isSysAdmin ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Mostrando todos os usuários
            </Typography>
          ) : municipioAtivo && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Mostrando usuários do município: {municipioAtivo.nome}
            </Typography>
          )}
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          Novo Usuario
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Acoes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id}>
                  <TableCell>{u.nome}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>
                    <Chip label={u.ativo ? 'Ativo' : 'Inativo'} color={u.ativo ? 'success' : 'default'} size="small" />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="primary" onClick={() => handleOpenDialog(u)}><EditIcon /></IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(u.id)}><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && users.length === 0 && (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">Nenhum usuario encontrado.</Typography>
        </Box>
      )}

      {/* Dialog Usuario */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser ? 'Editar Usuario' : 'Novo Usuario'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField label="Nome" fullWidth required value={formData.nome} onChange={(e) => setFormData({ ...formData, nome: e.target.value })} />
            <TextField label="Email" type="email" fullWidth required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
            <TextField
              label="CPF"
              fullWidth
              value={formData.cpf}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                if (value.length <= 11) setFormData({ ...formData, cpf: value });
              }}
              inputProps={{ maxLength: 11 }}
              helperText="Apenas numeros (11 digitos)"
            />
            <TextField
              label={editingUser ? 'Nova Senha (deixe em branco para manter)' : 'Senha'}
              type="password"
              fullWidth
              required={!editingUser}
              value={formData.senha}
              onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
              helperText={editingUser ? 'Deixe em branco para manter a senha atual' : 'Minimo de 6 caracteres'}
            />
            <FormControlLabel
              control={<Switch checked={formData.ativo} onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })} />}
              label="Usuario ativo"
            />

            {/* is_admin — apenas para SYSTEM_ADMIN */}
            {isSysAdmin && (
              <FormControlLabel
                control={<Switch checked={formData.is_admin || false} onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })} />}
                label="Administrador do sistema"
                sx={{ '& .MuiFormControlLabel-label': { fontWeight: 600, color: 'primary.main' } }}
              />
            )}

            {/* Municípios — filtrados pelo escopo do usuário */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                Municipios com acesso
              </Typography>
              <Autocomplete
                multiple
                options={availableMunicipios}
                getOptionLabel={(option) => `${option.nome} (IBGE: ${option.codigo_ibge})`}
                value={availableMunicipios.filter((m) => selectedMunicipios.includes(m.id))}
                onChange={(_, newValue) => setSelectedMunicipios(newValue.map((m) => m.id))}
                renderInput={(params) => <TextField {...params} placeholder="Selecione os municipios" variant="outlined" />}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} />
                    {option.nome} (IBGE: {option.codigo_ibge})
                  </li>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option.id} label={`${option.nome}`} size="small" />
                  ))
                }
              />
              {!isSysAdmin && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Voce so pode atribuir municipios aos quais tem acesso.
                </Typography>
              )}
            </Box>

            {/* Perfis por Município */}
            {selectedMunicipios.length > 0 && perfis.length > 0 && (
              <Box>
                <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary', fontWeight: 500 }}>
                  Perfis por Municipio
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {selectedMunicipios.map((municipioId) => {
                    const municipio = availableMunicipios.find((m) => m.id === municipioId);
                    if (!municipio) return null;
                    // Filtrar perfis disponíveis: globais + do município
                    const availablePerfis = perfis.filter(
                      (p: any) => p.municipio_id === null || p.municipio_id === municipioId
                    );
                    return (
                      <Box
                        key={municipioId}
                        sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, backgroundColor: 'background.paper' }}
                      >
                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                          {municipio.nome}
                          {municipio.codigo_ibge && <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>IBGE: {municipio.codigo_ibge}</Typography>}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {availablePerfis.map((perfil: any) => (
                            <FormControlLabel
                              key={perfil.id}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={perfisPorMunicipio[municipioId]?.[perfil.id] || false}
                                  onChange={(e) => {
                                    setPerfisPorMunicipio((prev) => ({
                                      ...prev,
                                      [municipioId]: { ...(prev[municipioId] || {}), [perfil.id]: e.target.checked },
                                    }));
                                  }}
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {perfil.nome}
                                    {perfil.is_default && <Chip label="Padrao" size="small" variant="outlined" sx={{ ml: 1, height: 20, fontSize: '0.625rem' }} />}
                                  </Typography>
                                  {perfil.descricao && <Typography variant="caption" color="text.secondary">{perfil.descricao}</Typography>}
                                </Box>
                              }
                            />
                          ))}
                          {availablePerfis.length === 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              Nenhum perfil disponivel para este municipio
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" disabled={!formData.nome || !formData.email || (!editingUser && !formData.senha)}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UsersPage;
